import Cookies from 'js-cookie';
import { get, filter, forEach, isEmpty, reduce, remove, defaultTo, every } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import Track from './track';
import { trackJs } from './utils';

const RECORDS = [
  {
    urlParam: ['utm_source', 'tr_source'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_medium', 'tr_medium'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_term', 'tr_term'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_content', 'tr_content'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_campaign', 'tr_campaign'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_campaignid', 'tr_campaignid'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_test', 'tr_test'],
    cmp: [],
  },
  {
    urlParam: ['utm_creative', 'tr_creative'],
    isRequired: false,
  },
  {
    urlParam: ['utm_sduration', 'tr_sduration'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_pviews', 'tr_pviews'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_cluster', 'tr_cluster'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_account', 'tr_account'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['utm_ad_type', 'tr_utm_ad_type'],
    isRequired: false,
    cmp: [],
  },
  {
    urlParam: ['gclid', 'utm_gclid', 'tr_gclid'],
    isRequired: true,
    cmp: [
      {
        name: 'Google Ads',
        id: 's1',
      },
      {
        name: 'Google Advertising Products',
        id: '755',
      },
    ],
  },
  {
    urlParam: ['msclkid', 'tr_msclkid'],
    isRequired: false,
    cmp: [
      {
        name: 'Microsoft Advertising',
        id: 's11',
      },
    ],
  },
  {
    urlParam: ['conv', 'tr_conv'],
    isRequired: false,
    cmp: [
      {
        name: 'Kevel',
        id: 'c31031',
      },
    ],
  },
  {
    urlParam: ['fbclid', 'tr_fbclid'],
    isRequired: false,
    cmp: [
      {
        name: 'Facebook',
        id: 's7',
      },
    ],
  },
  {
    urlParam: ['ttclid', 'tr_ttclid', 'pixel_id', 'tr_pixel_id', 'pixelid', 'tr_pixelid'],
    isRequired: false,
    cmp: [
      {
        name: 'TikTok',
        id: 's2536',
      },
    ],
  },
  {
    urlParam: ['bid', 'tr_bid'],
    isRequired: false,
    cmp: [
      {
        name: 'Firstlead GmbH',
        id: '766',
      },
    ],
  },
];

const getVendorConsents = () => {
  try {
    const cmp = window.__cmp('getCMPData');
    return defaultTo(cmp.vendorConsents, {});
  } catch (e) {
    trackJs('Can not get CMP during QID initialization');
    return {};
  }
};

const getRequiredWithNotEveryIdConsentGiven = (cmp) => {
  const consents = cmp ? get(cmp, 'vendorConsents', {}) : getVendorConsents();
  return filter(RECORDS, ({ cmp, isRequired }) => isRequired && every(cmp, ({ id }) => !consents[id]));
};

const getRequiredWithNotEmptyCmp = () => {
  return filter(RECORDS, ({ cmp, isRequired }) => isRequired && cmp.length);
};

const getUrlParamsFromRecords = (records) => {
  return reduce(records, (res, item) => [...res, ...item.urlParam], []);
};

const getAllUrlParams = () => getUrlParamsFromRecords(RECORDS);

const removeFromListUrlParamsToDelete = (paramsToAdd, paramsToDelete) => {
  return remove(paramsToAdd, (urlParam) => !paramsToDelete.includes(urlParam));
};

const getUrlParameter = (name) => {
  // TODO: Check what it does and how to simplify it
  // eslint-disable-next-line no-useless-escape
  const updatedName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + updatedName + '=([^&#]*)');
  const results = regex.exec(location.search);
  return results === null ? '' : decodeURIComponent(decodeURIComponent(results[1].replace(/\+/g, ' ')));
};

const checkForCmp = () => {
  return new Promise((resolve, reject) => {
    let count = 0;
    const interval = setInterval(() => {
      try {
        count += 1;
        const cmp = window.__cmp && window.__cmp('getCMPData');
        if (get(cmp, 'consentExists', false)) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve(cmp);
        }
      } catch {
        console.log("Fail window.__cmp('getCMPData'), attempt " + count);
      }
    }, 1250);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      clearTimeout(timeout);
      try {
        const cmp = window.__cmp && window.__cmp('consentStatus');
        if (cmp) {
          const { consentExists, userChoiceExists } = cmp;
          reject(new Error(`Timeout: consentExists:${consentExists}, userChoiceExists:${userChoiceExists}`));
        } else {
          reject(new Error(`Timeout: window.__cmp:${!!window.__cmp}`));
        }
      } catch {
        reject(new Error('Timeout: CMP data not found'));
      }
    }, 5000);
  });
};

export default class Qid {
  #l1Obj = Cookies.get('QID') ? JSON.parse(Cookies.get('QID')) : {};
  #convertVisitorId = '';

  constructor() {
    if (Qid.instance) return Qid.instance;
    Qid.instance = this;
    this.vendorConsents = {};
    this.removedClickIds = [];
    this.removeOptimizelyRelatedKeys();
    this.removeConvertRelatedKeys();
    this.initializeConvert();
    this.fillWithGoogleAnalytics();
    this.fillWithQueryParams(getAllUrlParams());
    checkForCmp()
      .then((cmp) => this.listenToCmpConsent(cmp))
      .catch((e) => trackJs('CMP was not found: ' + e.message));
    return this;
  }

  #customChange(cmp) {
    const recordsToDelete = getRequiredWithNotEveryIdConsentGiven(cmp);
    const toAdd = getAllUrlParams();
    const toDelete = filter(getUrlParamsFromRecords(recordsToDelete), (id) => !!getUrlParameter(id));
    if (isEmpty(toDelete)) {
      this.fillWithQueryParams(toAdd);
    } else {
      this.removedClickIds = toDelete;
      this.removeQueryParams(toDelete);
      this.fillWithQueryParams(removeFromListUrlParamsToDelete(toAdd, toDelete));
    }
    this.updateVendorConsents();
  }

  #acceptAll() {
    const urlParamsToAdd = getAllUrlParams();
    this.fillWithQueryParams(urlParamsToAdd);
    this.updateVendorConsents();
  }

  #rejectAll() {
    const recordsToDelete = getRequiredWithNotEmptyCmp();
    const toDelete = filter(getUrlParamsFromRecords(recordsToDelete), (id) => !!getUrlParameter(id));
    this.removedClickIds = toDelete;
    this.removeQueryParams(toDelete);
    this.updateVendorConsents();
  }

  updateVendorConsents() {
    const vendorConsents = getVendorConsents();
    this.vendorConsents = vendorConsents;
  }

  listenToCmpConsent(cmp) {
    try {
      this.#customChange(cmp);
      window.__cmp('addEventListener', ['consentcustom', () => this.#customChange(), false]);
      window.__cmp('addEventListener', ['consentrejected', () => this.#rejectAll(), false]);
      window.__cmp('addEventListener', ['consentapproved', () => this.#acceptAll(), false]);
    } catch (e) {
      trackJs('Error on listenToCmpConsent: ' + e.message);
    }
  }

  /**
   * Convert BYOID + lifecycle bootstrap.
   *
   * In production, a `<?php ?>` block at the top of `index.php` reads (or generates and
   * Set-Cookies) the `_conv_byoid` cookie and echoes the id into the inline BYOID script as
   * `window.__convertVisitorId`. Convert's snippet then loads and adopts it via the
   * `identify` push that was emitted alongside.
   *
   * In Vite dev mode PHP doesn't run, so the inline script ends up with the literal
   * `%%CONVERT_VISITOR_ID%%` placeholder. We detect that here (and any other malformed value
   * like an empty string) and fall back to client-side: mint a UUID, late-push it via
   * `identify`. Convert will adopt it, but the very first bucket event in this
   * page load will be tied to an anonymous, Convert-generated id.
   *
   * The `snippet.experiences_evaluated` listener refreshes the QID cookie with the active
   * experiences once Convert has decided them.
   */
  initializeConvert() {
    const fromHead = window.__convertVisitorId;
    const isValid = typeof fromHead === 'string' && fromHead.length > 0 && !fromHead.includes('%%');
    if (isValid) {
      this.#convertVisitorId = fromHead;
    } else {
      this.#convertVisitorId = uuidv4();
      Track.Experiments.ConvertIdentify(this.#convertVisitorId);
    }
    // Write convertVisitorId to QID eagerly. We already know the id (BYOID from the inline
    // script or the uuidv4 fallback above), so there's no reason to wait for
    // `snippet.experiences_evaluated` — which only fires when Convert has active experiences
    // to evaluate. On pages with no Convert experiments (or when Convert is gated by consent
    // and never initialises) the listener never fires, and convertVisitorId would otherwise
    // never reach the cookie. `updateCampaignsWithFreshConvertData` can still overwrite it
    // later with the same value.
    this.#l1Obj.convertVisitorId = this.#convertVisitorId;
    this.setL1Obj(this.#l1Obj);
    Track.Experiments.ConvertOnReady(this.updateCampaignsWithFreshConvertData.bind(this));
  }

  /**
   * Remove from QID key in Cookies all data with keys "optimizely" and "optimizelyVisitorId"
   */
  removeOptimizelyRelatedKeys() {
    if (this.#l1Obj.optimizely || this.#l1Obj.optimizelyVisitorId) {
      delete this.#l1Obj.optimizely;
      delete this.#l1Obj.optimizelyVisitorId;
      this.setL1Obj(this.#l1Obj);
    }
  }

  /**
   * Remove from QID key in Cookies all data with keys "convert" and "convertVisitorId"
   */
  removeConvertRelatedKeys() {
    if (this.#l1Obj.convert || this.#l1Obj.convertVisitorId) {
      delete this.#l1Obj.convert;
      delete this.#l1Obj.convertVisitorId;
      this.setL1Obj(this.#l1Obj);
    }
  }

  /**
   * Get keys from URL query string and add them to QID Cookie. If utmSource is present overwrite corresponding
   * keys. So utmSource Cookie keys shall dominate over same keys from URL query string
   */
  fillWithQueryParams(urlParamsToAdd) {
    forEach([...urlParamsToAdd, 'optimizely', 'optimizelyVisitorId', 'convertVisitorId'], (urlParamToAdd) => {
      const urlParamValue = getUrlParameter(urlParamToAdd);
      if (urlParamValue && urlParamValue !== 'optimizelyVisitorId' && urlParamValue !== 'convertVisitorId') {
        this.#l1Obj[urlParamToAdd] = urlParamValue;
      }
      if (urlParamValue === 'optimizelyVisitorId' || urlParamValue === 'convertVisitorId') {
        this.#l1Obj[urlParamToAdd] = urlParamValue.toString();
      }
    });
    const l1NeedToRemoveKeys = [];
    for (const key in this.#l1Obj) {
      const urlParamValue = getUrlParameter(key);
      const l1ObjValue = this.#l1Obj[key];
      if (['utm_cluster'].includes(key) && l1ObjValue && !urlParamValue) {
        l1NeedToRemoveKeys.push(key);
      }
    }
    if (l1NeedToRemoveKeys.length) {
      this.removeQueryParams(l1NeedToRemoveKeys);
    }
    const utmSources = this.convertUtmSourcesCookieRecord(Cookies.get('utmSources') || '');
    this.setL1Obj(!!utmSources ? { ...utmSources, ...this.#l1Obj } : this.#l1Obj);
  }

  removeQueryParams(paramsToDelete) {
    forEach(paramsToDelete, (p) => delete this.#l1Obj[p]);
    this.setL1Obj(this.#l1Obj);
  }

  /**
   * If ga key is present in window add "clientId" to QID Cookie
   */
  fillWithGoogleAnalytics() {
    if (typeof window.ga === 'function') {
      window.ga((info) => {
        this.setL1ObjKey('clientId', info.get('clientId'));
      });
    }
  }

  convertUtmSourcesCookieRecord(str) {
    if (!str) return null;
    return str
      .split('AND')
      .map((i) => {
        const pair = i.split('_');
        return pair.length === 2 ? pair : [pair[0], pair.slice(1).join('_')];
      })
      .map(([key, value]) => {
        if (key.startsWith('utm')) {
          return ['utm_' + key.slice(3), value];
        } else {
          return [key, value];
        }
      })
      .reduce((obj, [key, value]) => {
        return { ...obj, [key]: value };
      }, {});
  }

  mergeOptimizelyArrays(firstArray, secondArray) {
    const first = Array.isArray(firstArray) && firstArray.length ? firstArray : [];
    const second = Array.isArray(secondArray) && secondArray.length ? secondArray : [];
    const summary = [...first, ...second];
    return summary.reduce((result, record) => {
      const hasNotYetRecordWithExperimentId = result.findIndex((r) => r.experimentId === record.experimentId) < 0;
      if (hasNotYetRecordWithExperimentId) result.push(record);
      return result;
    }, []);
  }

  getActiveConvertExperiences() {
    try {
      return Track.Experiments.ConvertGetActiveExperiences();
    } catch (e) {
      return [];
    }
  }

  setL1ObjKey(key, value) {
    this.#l1Obj[key] = value;
    window.l1Obj = this.#l1Obj;
    Cookies.set('QID', JSON.stringify(this.#l1Obj));
  }

  setL1Obj(value) {
    this.#l1Obj = value;
    window.l1Obj = this.#l1Obj;
    Cookies.set('QID', JSON.stringify(this.#l1Obj), {
      expires: 365,
      SameSite: 'Lax',
    });
  }

  // USED OUTSIDE

  /**
   * Reshape Convert's native record `{ experienceId, variationId }` into the same shape
   * Optimizely stores `{ campaignId, experimentId, variationId }` so both vendors' records
   * can share a single bucket. Convert has no campaign concept, so `campaignId` is "".
   */
  convertExperienceToStorableObject({ experienceId, variationId }) {
    return { campaignId: '', experimentId: experienceId, variationId };
  }

  /**
   * Convert experiment data is merged into the SAME `optimizely` cookie key as Optimizely's own
   * campaigns — the BE only needs to read one field. Dedupe across both vendors is by
   * `experimentId` (via `mergeOptimizelyArrays`). Convert's visitor id keeps its own key and is
   * the BYOID we minted (either in the inline pre-snippet script or as a fallback in
   * `initializeConvert()`) and handed to Convert via `identify`.
   */
  updateCampaignsWithFreshConvertData() {
    const experiences = this.getActiveConvertExperiences().map((e) => this.convertExperienceToStorableObject(e));
    this.#l1Obj.convertVisitorId = this.#convertVisitorId;
    if (experiences.length) {
      const merged = this.mergeOptimizelyArrays(experiences, JSON.parse(this.#l1Obj.optimizely || '[]'));
      this.#l1Obj.optimizely = JSON.stringify(merged);
    }
    this.setL1Obj(this.#l1Obj);
  }

  get l1Obj() {
    return this.#l1Obj;
  }
}
